import React, { useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import Dialog from 'components/dialog'
import MuiDialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import CloseIcon from 'components/material-symbol/icons/Close'
import DeleteOutlineIcon from 'components/material-symbol/icons/DeleteOutline'
import PersonOutlineIcon from 'components/material-symbol/icons/PersonOutline'
import MessagingContactAvatar from '../messaging-contact-avatar'
import { useHistory } from 'react-router-dom'
import { useSnackbar, useSnackbarError } from 'lib/snackbar'
import {
  buildMessagingContactsByExternalId,
  getMessagingContactProfilePath,
} from '../messaging-utils'
import { isDistinctChannel } from '../utils'

const GET_MESSAGING_CONTACTS = gql`
  query MessagingContactsForMembersModal {
    messagingContacts {
      type
      id
      externalId
      externalIds
    }
  }
`

const useStyles = makeStyles(theme => ({
  avatar: {
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  dialogContent: {
    paddingLeft: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  itemText: {
    flex: '0 0 auto',
    [theme.breakpoints.only('xs')]: {
      flex: '1 1 auto',
    },
    marginRight: theme.spacing(1),
  },
  memberActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginLeft: 'auto',
    flexShrink: 0,
  },
  actionButton: {
    flexShrink: 0,
    width: 32,
    height: 32,
    padding: theme.spacing(0.75),
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    color: '#172024',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      color: '#0053FF',
      borderColor: '#BDBDBD',
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
    },
  },
  deleteButton: {
    '&:hover': {
      color: '#D32F2F',
    },
  },
  actionIcon: {
    fontSize: 18,
  },
}))

const MembersModal = ({ channel, trigger, currentUserId }) => {
  const styles = useStyles()
  const history = useHistory()
  const [memberToRemove, setMemberToRemove] = useState(null)
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const { data } = useQuery(GET_MESSAGING_CONTACTS)

  const isChatOwner = channel?.data?.created_by?.id === currentUserId
  const membersArray = Object.values(channel.state?.members || {})
  const showRemoveMembers =
    !isDistinctChannel(channel) && membersArray.length > 2 && isChatOwner

  const contactsByExternalId = useMemo(
    () => buildMessagingContactsByExternalId(data?.messagingContacts),
    [data?.messagingContacts],
  )

  const handleRemoveMember = async () => {
    if (!memberToRemove) return

    try {
      await channel.removeMembers([memberToRemove.user.id])
      enqueueSnackbar('Member removed', { variant: 'success' })
      setMemberToRemove(null)
    } catch (e) {
      snackbarError('Failed to remove member')
      window.rg4js &&
        window.rg4js('send', {
          error: e,
          tags: ['messaging', 'stream'],
        })
    }
  }

  const handleViewStudent = (closeDialog, profilePath) => {
    closeDialog()
    history.push(profilePath)
  }

  return (
    <Dialog trigger={trigger} fullWidth maxWidth="sm">
      {({ closeDialog }) => (
        <>
          <DialogTitle disableTypography style={{ paddingLeft: '32px' }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Members</Typography>
              <IconButton onClick={closeDialog} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers className={styles.dialogContent}>
            {membersArray.map(member => {
              const memberUser = member.user
              if (!memberUser) return null

              const contact = contactsByExternalId.get(memberUser.id)
              const profilePath =
                contact?.type === 'STUDENT'
                  ? getMessagingContactProfilePath(contact)
                  : null
              const isCurrentUser = memberUser.id === currentUserId
              const canRemoveMember = showRemoveMembers && !isCurrentUser

              return (
                <ListItem key={memberUser.id}>
                  <MessagingContactAvatar
                    aria-label="member"
                    className={styles.avatar}
                    image={memberUser.image}
                    name={memberUser.name}
                  />
                  <ListItemText
                    primary={memberUser.name}
                    classes={{ root: styles.itemText }}
                  />
                  {isCurrentUser && <ListItemText secondary="(You)" />}
                  {(profilePath || canRemoveMember) && (
                    <Box className={styles.memberActions}>
                      {profilePath && !isCurrentUser && (
                        <Tooltip title="View student details">
                          <IconButton
                            aria-label="View student details"
                            className={styles.actionButton}
                            onClick={() =>
                              handleViewStudent(closeDialog, profilePath)
                            }
                            size="small"
                          >
                            <PersonOutlineIcon
                              className={styles.actionIcon}
                              aria-hidden
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canRemoveMember && (
                        <Tooltip title="Remove member">
                          <IconButton
                            aria-label={`Remove ${memberUser.name}`}
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            onClick={() => setMemberToRemove(member)}
                            size="small"
                          >
                            <DeleteOutlineIcon
                              className={styles.actionIcon}
                              aria-hidden
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </ListItem>
              )
            })}
          </DialogContent>
          <MuiDialog
            open={Boolean(memberToRemove)}
            onClose={() => setMemberToRemove(null)}
            maxWidth="xs"
          >
            <DialogTitle>Remove Member</DialogTitle>
            <DialogContent>
              {`Are you sure you want to remove ${memberToRemove?.user?.name} from the group?`}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMemberToRemove(null)} color="primary">
                Cancel
              </Button>
              <Button
                onClick={handleRemoveMember}
                variant="contained"
                startIcon={<DeleteOutlineIcon />}
                style={{ backgroundColor: '#d32f2f', color: '#fff' }}
              >
                Remove
              </Button>
            </DialogActions>
          </MuiDialog>
        </>
      )}
    </Dialog>
  )
}

export default MembersModal
